<template>
  <div>
    <div v-if="history && history.length > 0">
      <div class="table-responsive">
        <table class="table table-centered table-bordered table-nowrap mb-0 table-hover">
          <thead class="thead-light">
            <tr>
              <th style="width: 150px">{{ $t("audit-control.history.date_user") }}</th>
              <th>{{ $t("audit-control.history.action.text") || "İşlem / Değişiklikler" }}</th>
              <th>{{ $t("risk.status.text") }}</th>
              <th>{{ $t("common.note") || "Açıklama" }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in paginatedHistory" :key="idx">
              <td>
                <small class="d-block text-muted">{{ item.date | date(true) }}</small>
                <span class="font-weight-bold" v-if="item.performedBy">{{ item.performedBy.name }} {{ item.performedBy.surname || "" }}</span>
                <span class="text-muted" v-else>-</span>
              </td>
              <td style="white-space: normal;">
                <b-badge variant="light" class="border text-dark mb-2">{{ translateAction(item.action) }}</b-badge>
                
                <!-- Detailed changes loop -->
                <div v-if="item.changes && Object.keys(item.changes).length > 0" class="mt-2">
                  <div v-for="(change, key) in item.changes" :key="key" class="font-size-12 mb-1 border-left pl-2">
                    <span class="text-muted font-weight-bold mr-1">{{ getFieldLabel(key) }}:</span>
                    <span class="text-danger strike-through">{{ formatValue(change.old, key) }}</span>
                    <span class="mx-1 text-muted"><i class="bx bx-right-arrow-alt"></i></span>
                    <span class="text-success">{{ formatValue(change.new, key) }}</span>
                  </div>
                </div>
                <div v-else-if="!item.description" class="text-muted small">
                  {{ $t("common.no-field-changes") || "Belirli bir alan değişikliği saptanmadı." }}
                </div>
              </td>

              <!-- Status column - monochrome -->
              <td>
                <b-badge v-if="getSnapshotValue(item, 'status')" variant="light" class="border text-dark">
                  {{ $t(`risk.status.${getSnapshotValue(item, "status").toLowerCase()}`) }}
                </b-badge>
                <span v-else>-</span>
              </td>

              <!-- Note column - monochrome -->
              <td>
                <div v-if="getSnapshotValue(item, 'note')">
                   {{ getLocalizedValue(getSnapshotValue(item, "note")) }}
                </div>
                <div v-if="item.description" :class="{ 'mt-1': getSnapshotValue(item, 'note') }" class="small">
                   {{ item.description }}
                </div>
                <span v-if="!getSnapshotValue(item, 'note') && !item.description">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="row mt-4">
        <div class="col">
          <b-pagination v-model="currentPage" :total-rows="history.length" :per-page="perPage" limit="10" size="sm"></b-pagination>
        </div>
      </div>
    </div>
    <div v-else class="text-center text-muted py-4 border rounded bg-light">
      <i class="bx bx-info-circle font-size-24 mb-2 d-block"></i>
      <h5>{{ $t("common.no-history") || "Henüz bir tarihçe kaydı bulunmuyor." }}</h5>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    history: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
    };
  },
  computed: {
    paginatedHistory() {
      // Sort desc by date
      const sorted = [...this.history].sort((a, b) => new Date(b.date) - new Date(a.date));
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return sorted.slice(start, end);
    },
    currentLanguage() {
      return this.$i18n.locale;
    },
  },
  methods: {
    translateAction(action) {
      if (!action) return "-";
      // Try to find translation in risk.history.action or audit-control.history.action or audit-finding.action
      const keys = [
        `risk.history.action.${action.toLowerCase()}`,
        `audit-control.history.action.${action}`,
        `audit-finding.action.${action.toLowerCase()}`
      ];
      for(const key of keys) {
        const trans = this.$t(key);
        if (trans !== key) return trans;
      }
      return action;
    },
    getFieldLabel(key) {
      const map = {
        name: this.$t("risk.name"),
        description: this.$t("risk.description"),
        category: this.$t("risk.category.text"),
        possibility: this.$t("risk.possibility"),
        effectValue: this.$t("risk.effect"),
        riskPoint: this.$t("risk.risk-point"),
        degree: this.$t("risk.degree.title"),
        riskDecision: this.$t("risk.applied-risk-decision.text"),
        appliedRiskValue: this.$t("risk.applied-risk-value.text"),
        weaknesses: this.$t("risk.weakness-definition.text"),
        threats: this.$t("risk.threat-definition.text"),
        source: this.$t("risk.source.text"),
        type: this.$t("risk.type.text"),
        kind: this.$t("risk.kind.text"),
        admin: this.$t("risk.admin"),
        owner: this.$t("risk.owner"),
        status: this.$t("risk.status.text"),
        controls: this.$t("risk.related-controls.text"),
        complianceItems: this.$t("risk.related-compliances"),
        acceptanceReason: this.$t("risk.acceptance.reason"),
        periodicAcceptanceDuration: this.$t("risk.acceptance.duration"),
        riskAssetGroupRelations: this.$te("asset-group.text") ? this.$t("asset-group.text") : "Varlık Grubu",
        note: this.$t("common.note") || "Açıklama",
      };
      return map[key] || key;
    },
    getSnapshotValue(item, field) {
      if (!item.snapshot) return null;
      return item.snapshot[field];
    },
    getLocalizedValue(val) {
      if (!val) return "";
      if (typeof val !== "object") return val;
      return val[this.$i18n.locale] || val.en || val.tr || "";
    },
    formatValue(val, key) {
      if (val === null || val === undefined) return "-";
      
      if (typeof val === "string") {
        const isEnum = /^[A-Z0-9_]+$/.test(val);
        if (isEnum) {
          const enumKey = val.toLowerCase().replace(/_/g, '-');
          if (key === "status") return this.$te(`risk.status.${enumKey}`) ? this.$t(`risk.status.${enumKey}`) : val;
          if (key === "riskDecision") {
            if (this.$te(`risk.applied-risk-decision.${enumKey}`)) return this.$t(`risk.applied-risk-decision.${enumKey}`);
            if (this.$te(`risk.decision.${enumKey}`)) return this.$t(`risk.decision.${enumKey}`);
            return val;
          }
          if (key === "appliedRiskValue") return this.$te(`risk.applied-risk-value.${enumKey}`) ? this.$t(`risk.applied-risk-value.${enumKey}`) : val;
          if (key === "degree") return this.$te(`risk.degree.${enumKey}`) ? this.$t(`risk.degree.${enumKey}`) : val;
        }
        return val;
      }

      if (Array.isArray(val)) {
        if (val.length === 0) return "[]";
        return val.map(v => {
          if (typeof v === 'object' && v !== null) {
            if (v.assetGroup && v.assetGroup.name) return this.getLocalizedValue(v.assetGroup.name);
            if (v.name) return this.getLocalizedValue(v.name);
            if (v.no) return v.no;
            if (v.tr || v.en || v[this.currentLanguage]) return v[this.currentLanguage] || v.tr || v.en;
            if (v._id) return v._id;
          }
          return v;
        }).join(", ");
      }
      if (typeof val === "object" && val !== null) {
        if (val.tr || val.en || val[this.currentLanguage]) {
          return val[this.currentLanguage] || val.tr || val.en;
        }
        if (val.name) return this.getLocalizedValue(val.name);
        if (val.no) return val.no;
        if (val._id) return val._id;
        return JSON.stringify(val);
      }
      return val;
    },
  },
};
</script>

<style scoped>
.strike-through {
  text-decoration: line-through;
  opacity: 0.7;
}
</style>
